import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";

const UserProfileContext = createContext(null);

// Every role and settings consumer observes one account-scoped document.
// Tag state with its UID so an account change hides old data before effects run.
export function UserProfileProvider({ uid, children }) {
  const [state, setState] = useState({ uid, profile: null, loading: Boolean(uid), error: null });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  useEffect(() => {
    setState({ uid, profile: null, loading: Boolean(uid), error: null });
    if (!uid) return undefined;
    let active = true;
    const unsubscribe = onSnapshot(doc(getFirestore(), "users", uid), { includeMetadataChanges: true }, snapshot => {
      if (active) setState({ uid, profile: snapshot.exists() ? snapshot.data() : null, authoritative: !snapshot.metadata?.fromCache && !snapshot.metadata?.hasPendingWrites, loading: false, error: null });
    }, error => {
      if (active) setState({ uid, profile: null, loading: false, error });
    });
    return () => { active = false; unsubscribe(); };
  }, [uid, attempt]);
  const value = useMemo(() => ({
    ...(state.uid === uid ? state : { uid, profile: null, loading: Boolean(uid), error: null }),
    retry,
  }), [state, uid, retry]);
  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) throw new Error("useUserProfile must be used inside AuthProvider");
  return context;
}
